import { useCallback, useEffect, useRef, useState } from "react";

// Disable a submit button while a mutation is in flight AND for a
// short cooldown after it resolves, so a second click during the
// post-submit settle can't fire a duplicate request.
export function useSubmitLock(cooldownMs = 500) {
  const [locked, setLocked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const arm = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setLocked(true);
    timerRef.current = setTimeout(() => {
      setLocked(false);
      timerRef.current = null;
    }, cooldownMs);
  }, [cooldownMs]);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      // Already locked → silently no-op so callers can `void run(...)`
      // without worrying about unhandled-rejection edge cases.
      if (locked) return undefined;
      setLocked(true);
      try {
        return await fn();
      } catch {
        // Swallow rejections so callers can `void run(...)` safely.
        // Mutations attach their own onError for user-visible feedback;
        // we only care about the lock state here.
        return undefined;
      } finally {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setLocked(false);
          timerRef.current = null;
        }, cooldownMs);
      }
    },
    [locked, cooldownMs],
  );

  return { locked, arm, run };
}
